import React, { useCallback, useMemo } from "react";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const TradingMarketNegotiationMetadataTable = ({
  allowEdit,
  fields,
  onDelete,
  onEdit,
}) => {
  const { t } = useTranslation(["trading"]);

  const rowData = useMemo(
    () =>
      (fields || []).map((field) => ({
        ...field,
        optionsDisplay: (field.options || [])
          .map((option) => option.label || option.value)
          .join(", "),
      })),
    [fields]
  );

  const actionRenderer = useCallback(
    (params) => {
      if (!allowEdit || !params.data) return null;

      return (
        <div className="d-flex justify-content-center">
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            onClick={(event) => {
              event.stopPropagation();
              onEdit(params.data);
            }}
          >
            {t("Edit")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-delete"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(params.data);
            }}
          >
            {t("Delete")}
          </button>
        </div>
      );
    },
    [allowEdit, onDelete, onEdit, t]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "fieldGroupingName",
        headerName: t("FieldGroupingName"),
        sortable: true,
        sort: "asc",
        filter: true,
        flex: 1.5,
      },
      {
        field: "sortOrder",
        headerName: t("SortOrder"),
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        field: "fieldName",
        headerName: t("FieldName"),
        sortable: true,
        filter: true,
        flex: 1.5,
      },
      {
        field: "fieldDataType",
        headerName: t("FieldDataType"),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params) => t(params.value),
      },
      {
        field: "defaultValue",
        headerName: t("DefaultValue"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "optionsDisplay",
        headerName: t("Values"),
        sortable: false,
        filter: true,
        flex: 2,
      },
      {
        headerName: t("Actions"),
        sortable: false,
        filter: false,
        width: 180,
        cellRenderer: actionRenderer,
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [actionRenderer, t]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  return (
    <ThemeableAgGrid
      className="table-bold"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={rowData}
      getRowId={(params) => params.data.fieldDefinitionId}
      animateRows={true}
      domLayout="autoHeight"
      pagination={true}
      paginationPageSize={10}
      paginationPageSizeSelector={[5, 10, 20, 50, 100]}
      suppressPaginationPanel={false}
      overlayNoRowsTemplate={t("NoTradeNegotiationMetadataFields")}
    />
  );
};

export default TradingMarketNegotiationMetadataTable;
